import { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut, MonitorSmartphone, X, KeyRound, ShieldCheck } from "lucide-react";
import { API, formatApiErrorDetail } from "../auth/api";
import { useAuth } from "../auth/AuthContext";
import MfaSetup from "../pages/MfaSetup";

export default function AccountDialog({ open, onClose, onChangePassword }) {
  const { user, logoutEverywhere, refresh } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaAction, setMfaAction] = useState(null); // "recovery" | "disable"
  const [mfaPassword, setMfaPassword] = useState("");
  const [newCodes, setNewCodes] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/auth/sessions");
      setSessions(data);
      const devs = await API.get("/auth/trusted-devices");
      setDevices(devs.data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submitMfaAction = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const url = mfaAction === "disable" ? "/auth/mfa/disable" : "/auth/mfa/recovery-codes";
      const { data } = await API.post(url, { password: mfaPassword });
      setMfaPassword("");
      setMfaAction(null);
      if (data.recovery_codes) setNewCodes(data.recovery_codes);
      await refresh();
      await load();
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    }
  };

  const revoke = async (id) => {
    try {
      await API.delete(`/auth/sessions/${id}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const forgetDevice = async (id) => {
    try {
      await API.delete(id ? `/auth/trusted-devices/${id}` : "/auth/trusted-devices");
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-start justify-center bg-black/70 backdrop-blur-sm p-6 overflow-auto"
      data-testid="account-dialog"
      role="dialog"
      aria-modal="true"
      aria-label="My account"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-xl border border-white/10 bg-[#0a0a0c] my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="min-w-0">
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-zinc-200">
              My Account
            </div>
            <div className="font-mono text-[10px] text-zinc-500 truncate mt-0.5">
              {user?.name} · {user?.email} · {user?.role}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="account-dialog-close"
            className="text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 flex flex-wrap gap-2 border-b border-white/10">
          <button
            type="button"
            onClick={onChangePassword}
            data-testid="account-change-password"
            className="flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-cyan-400/60 font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-300 hover:text-cyan-300 transition-colors"
          >
            <KeyRound className="w-3 h-3" /> Change Password
          </button>
          <button
            type="button"
            onClick={logoutEverywhere}
            data-testid="account-logout-all"
            className="flex items-center gap-2 px-3 py-2 border border-rose-400/40 hover:bg-rose-400/10 font-mono text-[10px] tracking-[0.18em] uppercase text-rose-200 transition-colors"
          >
            <LogOut className="w-3 h-3" /> Sign Out Everywhere
          </button>
        </div>

        <div className="px-5 py-4 border-b border-white/10 space-y-3" data-testid="mfa-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <ShieldCheck
                className={`w-3.5 h-3.5 ${user?.mfa_enabled ? "text-emerald-400" : "text-amber-400"}`}
              />
              <span
                className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-300"
                data-testid="mfa-status"
              >
                Two-Step Verification ·{" "}
                <span className={user?.mfa_enabled ? "text-emerald-300" : "text-amber-300"}>
                  {user?.mfa_enabled ? "On" : "Off"}
                </span>
                {user?.mfa_required && <span className="text-zinc-500"> · required</span>}
              </span>
            </div>
            <div className="flex gap-2">
              {!user?.mfa_enabled && (
                <button
                  type="button"
                  onClick={() => setShowMfaSetup(true)}
                  data-testid="mfa-turn-on"
                  className="px-3 py-2 border border-emerald-400/50 hover:bg-emerald-400/10 font-mono text-[10px] tracking-[0.18em] uppercase text-emerald-200 transition-colors"
                >
                  Turn On
                </button>
              )}
              {user?.mfa_enabled && (
                <button
                  type="button"
                  onClick={() => setMfaAction("recovery")}
                  data-testid="mfa-new-recovery-codes"
                  className="px-3 py-2 border border-white/10 hover:border-cyan-400/60 font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-300 hover:text-cyan-300 transition-colors"
                >
                  New Recovery Codes
                </button>
              )}
              {user?.mfa_enabled && !user?.mfa_required && (
                <button
                  type="button"
                  onClick={() => setMfaAction("disable")}
                  data-testid="mfa-turn-off"
                  className="px-3 py-2 border border-rose-400/40 hover:bg-rose-400/10 font-mono text-[10px] tracking-[0.18em] uppercase text-rose-200 transition-colors"
                >
                  Turn Off
                </button>
              )}
            </div>
          </div>

          {mfaAction && (
            <form onSubmit={submitMfaAction} className="flex gap-2" data-testid="mfa-confirm-form">
              <input
                type="password"
                value={mfaPassword}
                autoComplete="current-password"
                onChange={(e) => setMfaPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                data-testid="mfa-confirm-password"
                className="flex-1 bg-[#09090B] border border-white/10 focus:border-emerald-400 outline-none px-3 py-2 font-mono text-[12px] text-zinc-100 placeholder:text-zinc-700"
              />
              <button
                type="submit"
                data-testid="mfa-confirm-submit"
                className="px-3 py-2 border border-emerald-400/60 bg-emerald-400/10 hover:bg-emerald-400/20 font-mono text-[10px] tracking-[0.18em] uppercase text-emerald-200"
              >
                Confirm
              </button>
            </form>
          )}

          {newCodes && (
            <div className="space-y-2" data-testid="mfa-new-codes">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300">
                Save these — shown once
              </div>
              <div className="grid grid-cols-2 gap-2">
                {newCodes.map((c) => (
                  <div
                    key={c}
                    className="border border-white/10 bg-[#09090B] px-2 py-1.5 font-mono text-[11px] text-emerald-200 text-center"
                  >
                    {c}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="px-5 pt-4 font-mono text-[11px] text-rose-300" data-testid="account-error">
            {error}
          </div>
        )}

        <div className="px-5 py-2 font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500">
          Active sessions
        </div>
        <div className="divide-y divide-white/5" data-testid="sessions-list">
          {loading ? (
            <div className="px-5 py-6 flex items-center gap-2 font-mono text-[11px] text-zinc-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </div>
          ) : (
            sessions.map((s) => (
              <div
                key={s.id}
                className="px-5 py-3 flex items-center gap-3"
                data-testid={`session-row-${s.id}`}
              >
                <MonitorSmartphone className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[11px] text-zinc-300 truncate">
                    {s.ip}
                    {s.current && <span className="ml-2 text-emerald-300">· this device</span>}
                  </div>
                  <div className="font-mono text-[9px] text-zinc-600 truncate">
                    last seen {new Date(s.last_seen_at).toLocaleString()}
                  </div>
                </div>
                {!s.current && (
                  <button
                    type="button"
                    onClick={() => revoke(s.id)}
                    data-testid={`revoke-session-${s.id}`}
                    className="px-2.5 py-1 border border-white/10 hover:border-rose-400/60 font-mono text-[9px] tracking-[0.16em] uppercase text-zinc-400 hover:text-rose-300 transition-colors"
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))
          )}
        </div>
        <div className="px-5 py-2 flex items-center justify-between gap-3">
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500">
            Trusted devices{devices.length > 0 && ` · ${devices.length}`}
          </span>
          {devices.length > 0 && (
            <button
              type="button"
              onClick={() => forgetDevice(null)}
              data-testid="forget-all-devices"
              className="px-2.5 py-1 border border-white/10 hover:border-rose-400/60 font-mono text-[9px] tracking-[0.16em] uppercase text-zinc-400 hover:text-rose-300 transition-colors"
            >
              Forget All
            </button>
          )}
        </div>
        <div className="divide-y divide-white/5" data-testid="trusted-devices-list">
          {devices.length === 0 ? (
            <div className="px-5 pb-4 font-mono text-[10px] leading-relaxed text-zinc-600">
              {user?.mfa_enabled
                ? "No remembered devices — a code is required at every sign-in. Tick “remember this device” on the code screen to skip it for 30 days."
                : "Available once two-step verification is on."}
            </div>
          ) : (
            devices.map((d) => (
              <div
                key={d.id}
                className="px-5 py-3 flex items-center gap-3"
                data-testid={`trusted-device-row-${d.id}`}
              >
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400/70 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[11px] text-zinc-300 truncate">
                    {d.ip}
                    {d.current && <span className="ml-2 text-emerald-300">· this device</span>}
                  </div>
                  <div className="font-mono text-[9px] text-zinc-600 truncate">
                    last used {new Date(d.last_used_at).toLocaleDateString()} · expires{" "}
                    {new Date(d.expires_at).toLocaleDateString()}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => forgetDevice(d.id)}
                  data-testid={`forget-device-${d.id}`}
                  className="px-2.5 py-1 border border-white/10 hover:border-rose-400/60 font-mono text-[9px] tracking-[0.16em] uppercase text-zinc-400 hover:text-rose-300 transition-colors"
                >
                  Forget
                </button>
              </div>
            ))
          )}
        </div>
      </div>
      {showMfaSetup && (
        <MfaSetup
          onDone={() => {
            setShowMfaSetup(false);
            load();
          }}
          onCancel={() => setShowMfaSetup(false)}
        />
      )}
    </div>
  );
}
